import styles from '../styles/Skills.module.css'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import DarkMode from '@/components/DarkMode'

import skills from './skills.json'
import skillLabels from './labels.json'

function getReferenceUrl(shortUrl) {
  if (shortUrl.startsWith('on/')) {
    return shortUrl.replace('on/', 'https://on.cypress.io/')
  }
  if (shortUrl.startsWith('blog/')) {
    return shortUrl.replace('blog/', 'https://glebbahmutov.com/blog/')
  }
  return shortUrl
}

function Skill({ skill }) {
  let markdownSkill = Array.isArray(skill) ? skill[0] : skill
  if (Array.isArray(skill)) {
    skill.slice(1).forEach((reference, k) => {
      const fullUrl = getReferenceUrl(reference)
      markdownSkill += ` [${k + 1}](${fullUrl})`
    })
  }

  /* do not wrap skills in <P> elements, only allow links */
  return (
    <ReactMarkdown
      skipHtml={true}
      allowedElements={['p', 'a']}
      components={{
        p: (props) => <> {props.children} </>,
        a: ({ node, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer" />
        ),
      }}
    >
      {markdownSkill}
    </ReactMarkdown>
  )
}

export default function Home() {
  const [checkedState, setCheckedState] = useState(
    new Array(skills.length).fill(false),
  )

  const handleOnChange = (position) => {
    const updatedCheckedState = checkedState.map((item, index) =>
      index === position ? !item : item,
    )

    setCheckedState(updatedCheckedState)
  }

  // each skill can be a text line
  // or an Array with the first element being the skill, and the rest
  // are the URLs to the resources to study for it

  const skillCount = checkedState
    .map((set) => (set ? 1 : 0))
    .reduce((a, b) => a + b, 0)
  const skillPercentage = Math.round((skillCount / skills.length) * 100)
  const skillLabel = Object.keys(skillLabels)
    .reverse()
    .find((key) => skillLabels[key] <= skillPercentage)

  function copyResultsToClipboard() {
    let text = 'Cypress Skills\nhttps://cypress.tips/skills\n\n'

    text += `${skillCount} / ${skills.length} skills checked = ${skillPercentage}% ${skillLabel}\n\n`

    skills.forEach((skill, k) => {
      const hasSkill = checkedState[k]
      text += `${hasSkill ? '✅' : '⭕️'} ${skill}\n`
    })
    navigator.clipboard.writeText(text)
    toast.info('Copied to clipboard!', {
      autoClose: 3000,
      hideProgressBar: true,
    })
  }

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <DarkMode />
      </div>
      <header className={styles.header}>
        <h1>Cypress Skills</h1>
        <div className={styles.info}>
          Please check all the skills that you have experience with 📚. For more
          info, read{' '}
          <a href="https://glebbahmutov.com/blog/cypress-skills-ladder/">
            Cypress Skills Ladder
          </a>{' '}
          and <a href="https://glebbahmutov.com/blog/how-i-hire/">How I Hire</a>
        </div>
      </header>

      <main className={styles.main}>
        <form className={styles.skills}>
          <ul>
            {skills.map((skill, k) => (
              <li key={k} data-cy="skill">
                <label>
                  <input
                    type="checkbox"
                    name={`skill_${k}`}
                    checked={checkedState[k]}
                    onChange={() => handleOnChange(k)}
                  />
                  <Skill skill={skill} />
                </label>
              </li>
            ))}
          </ul>
        </form>
      </main>
      <footer className={styles.footer}>
        <span className={styles.skillCount}>
          <span data-cy="checked">{skillCount}</span> / {skills.length} (
          {skillPercentage}%)
        </span>
        <span className={styles.skillLabel} data-cy="skill-label">
          {skillLabel}
        </span>
        <button
          data-cy="copy-results"
          className={styles.copy}
          title="Copy results to clipboard"
          onClick={() => copyResultsToClipboard()}
        >
          📋
        </button>
      </footer>
      <ToastContainer />
    </div>
  )
}
